/*
 * The On-site Settings screen's state, shared with the classic panel.
 *
 * Two things live here and nowhere else.
 *
 * **The version handshake.** The PATCH refuses a stale version rather than overwriting, and answers
 * 409 with the record that won in its body. That is not an error to report and forget: somebody
 * else saved while this form was open, and the operator has to see their values before deciding to
 * overwrite them. So a conflict is state, not a message — and the local version advances to the one
 * that won, which makes the next click a deliberate overwrite instead of a second conflict.
 *
 * **What a target is.** `targetRevenue: null` clears the target, which is a different statement
 * from a target of zero, so the field is held as a string: an empty box can mean "no target", which
 * a number cannot express.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, opsApi } from '../../../api';
import type {
  OpsEventSettingsDto, OpsRiskDto, OpsStaffSummaryDto, OpsTaskDto,
} from '../../../api';
import { computePhaseReadiness } from '../../../readiness';
import type { PhaseReadiness, ReadinessInput } from '../../../readiness';

export interface ParsedTarget {
  /** `null` is "no target set", which the endpoint accepts and which is not zero. */
  value: number | null;
  invalid: boolean;
}

/**
 * The one rule for reading the target box.
 *
 * Spaces and thousands separators are noise a person types and are taken off; nothing else is.
 * Stripping every non-digit instead would turn a pasted `12.5` into `125` — a silent tenfold error
 * in a money field — so anything that is not a whole number of baht is reported as invalid and the
 * save is blocked, rather than quietly becoming a different number.
 */
export function parseTarget(raw: string): ParsedTarget {
  const cleaned = raw.replace(/[\s,]/g, '');
  if (cleaned === '') return { value: null, invalid: false };
  const n = Number(cleaned);
  if (!/^\d+$/.test(cleaned) || !Number.isSafeInteger(n)) return { value: null, invalid: true };
  return { value: n, invalid: false };
}

export interface OpsEventSettingsState {
  data: OpsEventSettingsDto | null;
  target: string;
  isOutdoor: boolean;
  parsed: ParsedTarget;
  dirty: boolean;
  saving: boolean;
  saved: boolean;
  error: string;
  loadError: string;
  /** Set when someone else wrote first. Carries their values, so the operator sees what they'd lose. */
  conflict: OpsEventSettingsDto | null;
  setTarget: (raw: string) => void;
  setIsOutdoor: (next: boolean) => void;
  /** Back to the values as last read. Offered only while dirty. */
  discard: () => void;
  /** Take the other person's values instead of overwriting them. */
  takeTheirs: () => void;
  save: () => Promise<void>;
  reload: () => Promise<void>;
}

export function useOpsEventSettings(token: string, eventSlug: string | undefined): OpsEventSettingsState {
  const [data, setData] = useState<OpsEventSettingsDto | null>(null);
  const [target, setTargetRaw] = useState('');
  const [isOutdoor, setOutdoor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [conflict, setConflict] = useState<OpsEventSettingsDto | null>(null);

  const apply = useCallback((d: OpsEventSettingsDto) => {
    setData(d);
    setTargetRaw(d.targetRevenue === null ? '' : String(d.targetRevenue));
    setOutdoor(d.isOutdoor);
  }, []);

  const load = useCallback(async () => {
    try {
      apply(await opsApi.getEventSettings(token, eventSlug));
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดการตั้งค่าไม่สำเร็จ');
    }
  }, [token, eventSlug, apply]);

  useEffect(() => { void load(); }, [load]);

  const parsed = parseTarget(target);
  const savedTarget = data === null || data.targetRevenue === null ? '' : String(data.targetRevenue);
  const dirty = data !== null
    && (parsed.invalid
      ? target.trim() !== savedTarget
      : String(parsed.value ?? '') !== savedTarget || isOutdoor !== data.isOutdoor);

  const setTarget = useCallback((raw: string) => { setTargetRaw(raw); setSaved(false); }, []);
  const setIsOutdoor = useCallback((next: boolean) => { setOutdoor(next); setSaved(false); }, []);
  const discard = useCallback(() => { if (data) apply(data); setSaved(false); setError(''); }, [data, apply]);
  const takeTheirs = useCallback(() => {
    if (conflict) { apply(conflict); setConflict(null); setSaved(false); }
  }, [conflict, apply]);

  const save = useCallback(async () => {
    if (!data || parsed.invalid) return;
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      apply(await opsApi.updateEventSettings(
        token,
        { targetRevenue: parsed.value, isOutdoor, version: data.version },
        eventSlug,
      ));
      setConflict(null);
      setSaved(true);
    } catch (e) {
      /*
       * The server puts the winning record in the 409 body for exactly this, so there is nothing to
       * re-fetch — and the version is advanced to the one that won so the retry is an overwrite the
       * operator chose, not a second collision.
       */
      const body = e instanceof ApiError && e.status === 409
        ? (e.body as { code?: string; current?: OpsEventSettingsDto } | undefined)
        : undefined;
      const current = body?.code === 'OPS_SETTINGS_VERSION_CONFLICT' ? body.current : undefined;
      if (current) { setConflict(current); setData({ ...data, version: current.version }); }
      else setError(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }, [data, parsed.invalid, parsed.value, isOutdoor, token, eventSlug, apply]);

  return {
    data, target, isOutdoor, parsed, dirty, saving, saved, error, loadError, conflict,
    setTarget, setIsOutdoor, discard, takeTheirs, save, reload: load,
  };
}

/**
 * The `prepare` phase's pre-publish checks, for the screen that owns one of them.
 *
 * `readiness.ts` already decides what ready means; this reads the three lists that phase's specs
 * need and hands them over with the settings record the form is holding — so saving a target moves
 * the check that counts it without a round trip.
 *
 * A 403 is data here, not a failure: it becomes `null`, which the rules read as "this token cannot
 * see it" and report as seen-in-part rather than as a check that failed. Anything else is an error.
 */
export interface PrepareReadinessState {
  readiness: PhaseReadiness | null;
  loading: boolean;
  error: string;
}

const permitted = async <T,>(p: Promise<T>): Promise<T | null> => {
  try {
    return await p;
  } catch (e) {
    if (e instanceof ApiError && e.status === 403) return null;
    throw e;
  }
};

export function usePrepareReadiness(
  token: string,
  eventSlug: string | undefined,
  opsSettings: OpsEventSettingsDto | null,
): PrepareReadinessState {
  const [lists, setLists] = useState<
    { tasks: OpsTaskDto[] | null; risks: OpsRiskDto[] | null; staff: OpsStaffSummaryDto[] | null } | null
  >(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [tasks, risks, staff] = await Promise.all([
          permitted(opsApi.listTasks(token, eventSlug)),
          permitted(opsApi.listRisks(token, eventSlug)),
          permitted(opsApi.listStaff(token, eventSlug)),
        ]);
        if (!live) return;
        setLists({ tasks, risks, staff });
        setError('');
      } catch (e) {
        if (!live) return;
        setError(e instanceof ApiError ? e.message : 'โหลดสถานะความพร้อมไม่สำเร็จ');
      }
    })();
    return () => { live = false; };
  }, [token, eventSlug]);

  if (!lists) return { readiness: null, loading: !error, error };

  const input: ReadinessInput = {
    content: null, tickets: null, paymentAccounts: null, seatZones: null,
    ticketDelivery: null, salesChannels: null, cues: null, docks: null,
    opsSettings, tasks: lists.tasks, risks: lists.risks, staff: lists.staff,
  };
  return { readiness: computePhaseReadiness('prepare', input), loading: false, error };
}
